import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./index.css";
import "@styles/colours.module.css";
import ErrorPage from "@pages/Error/ErrorPage";
import AppLayout from "@components/layout/AppLayout/AppLayout";
import authRoutes from "@routes/authRoutes";
import charitiesRoutes from "@routes/charitiesRoutes";
import businessesRoutes from "@routes/businessesRoutes";
import directorsRoutes from "@routes/directorsRoutes";
import upcomingServiceRoutes from "@routes/upcomingServiceRoutes";
import supportRoutes from "@routes/supportRoutes";
import memorialsRoutes from "@routes/memorialsRoutes";
import accountRoutes from "@routes/accountRoutes";
import AuthLayout from "@components/layout/AuthLayout/AuthLayout";
import StyleGuidePage from "@pages/Dev/StyleGuidePage";
import HomePage from "@pages/Home/HomePage";
import { AuthProvider } from "@context/auth-context";
import { BrandingOverlayProvider } from "@context/branding-overlay-context";
import MaintenancePage from "@pages/Error/MaintenancePage";
import UnauthorisedPage from "@pages/Error/UnauthorisedPage";
import anniversariesRoutes from "@routes/anniversariesRoutes";
import usePageTracking from "@hooks/use-page-tracking";
import notificationsRoutes from "@routes/notificationsRoutes";
import ManageTributePage from "@pages/Memorials/ManageTributePage";
import ConsentBanner from "@components/display/ConsentBanner/ConsentBanner";
import { initGA } from "@util/ga";

// AU hub: starts immediately as before.
// International hub: starts only after consent (ConsentBanner).
initGA();

if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") {
    console.log(`App running in ${process.env.NODE_ENV} mode.`);
}

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
);

function AnalyticsTracker() {
    usePageTracking();
    return null; // doesn’t render anything, just runs the hook
}

// The only pages meant for search results; keep in step with generate-sitemap.js
const INDEXABLE_PATHS = ["/", "/auth/sign-in", "/auth/sign-up", "/auth/forgot-password"];

// Self-referencing canonical (current host and path, no query or trailing slash),
// so it is correct on every hub domain. Every other page (signed-in pages, email-link
// and confirmation pages) is marked noindex
function SeoTags() {
    const { pathname } = useLocation();
    const path = pathname.replace(/\/+$/, "") || "/";
    return (
        <Helmet>
            <link rel="canonical" href={`${window.location.origin}${path}`} />
            {!INDEXABLE_PATHS.includes(path) && <meta name="robots" content="noindex" />}
        </Helmet>
    );
}

const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <>
                <AnalyticsTracker />
                <SeoTags />
                <ConsentBanner />
                <Outlet />
            </>
        ),
        errorElement: <ErrorPage />,
        children: [
            {
                path: "",
                element: <AuthLayout />,
                children: [
                    {
                        path: "",
                        element: <HomePage />,
                    },
                ],
            },
            {
                element: <AuthLayout />,
                children: [...authRoutes, { path: "tribute/manage", element: <ManageTributePage /> }],
            },
            {
                element: <AppLayout />,
                children: [
                    ...charitiesRoutes,
                    ...businessesRoutes,
                    ...directorsRoutes,
                    ...anniversariesRoutes,
                    ...upcomingServiceRoutes,
                    ...memorialsRoutes,
                    ...accountRoutes,
                    ...notificationsRoutes,
                    ...supportRoutes,
                ],
            },
            {
                path: "/maintenance",
                element: <MaintenancePage />,
            },
            {
                path: "/unauthorised",
                element: <UnauthorisedPage />,
            },
            ...(process.env.NODE_ENV === "development" ? [
                {
                    path: "/style-guide",
                    element: (
                        <React.Suspense fallback={<div>Loading Style Guide...</div>}>
                            <StyleGuidePage />
                        </React.Suspense>
                    ),
                },
            ] : []),
        ],
    },
]);

root.render(
    <React.StrictMode>
        <AuthProvider>
            <BrandingOverlayProvider>
                <RouterProvider router={router} />
            </BrandingOverlayProvider>
        </AuthProvider>
    </React.StrictMode>
);
